import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import React, { Dispatch, FormEventHandler, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { PsCheckUpdate } from '../../../../../api/model';
import { GetPsCheckDownloadParams } from '../../../../../api/model/getPsCheckDownloadParams';
import { GetPsCheckParams } from '../../../../../api/model/getPsCheckParams';
import {
  GetPsCheckDownloadQueryResult,
  useGetPsCheck,
  useGetPsCheckDownload,
  usePostPsCheckCheck,
  usePostPsCheckRemand
} from '../../../../../api/ps-checks/ps-checks';
import { useDefaultOptions, useOptionsForBinary } from '../../../../../hooks/api';
import yup from '../../../../../utils/custom-yup';
import { executeDownload } from '../../../../../utils/download';
import NoDataComponent from '../../../../atoms/NoDataComponent';
import BlobErrorModal from '../../../../modules/BlobErrorModal';
import ConfirmModal from '../../../../modules/ConfirmModal';
import ErrorModal from '../../../../modules/ErrorModal';
import Modal from '../../../../modules/Modal';
import OkModal from '../../../../modules/OkModal';
import SpinnerModal from '../../../../modules/SpinnerModal';
import { DialogType, Message, formatDate, getDialogMessage, truncateDate } from '../PsListCommmon';

interface Props {
  onClose: () => void;
  displayCloseButton: boolean;
  versionId: number;
  state: GetPsCheckParams | undefined;
  isViewMode: boolean;
  setRefresh: () => void;
  setActiveModal: Dispatch<SetStateAction<number>>;
  setMessage: Dispatch<SetStateAction<string>>;
  targetIndex: number;
  checkNextPs: (targetIndex: number) => Promise<void>;
}

/**
 * 各入力項目定数化
 */
const PREPARATION_ITEM_INPUT_NAMES = {
  comment: 'comment',
};

/**
 * 内容確認(G/L)ダイアログ
 */
function PsCheckDialog({
  onClose,
  displayCloseButton,
  versionId,
  state,
  isViewMode,
  setRefresh,
  setActiveModal,
  setMessage,
  targetIndex,
  checkNextPs,
}: Props) {
  // 翻訳用定数
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const options = useDefaultOptions(language);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const ButtonType = useRef('');
  const [confirmDalogFlg, setConfirmDalogFlg] = useState<boolean>(false);
  const [displayValidationError, setDisplayValidationError] = useState<boolean>(false);
  // スピナーモーダルを表示する
  const [displaySpinnerModal, setDisplaySpinnerModal] = useState(false);
  const [remandErrorModal, setRemandErrorModal] = useState(false);
  const [saveErrorModal, setSaveErrorModal] = useState(false);
  const [displayDownloadError, setDisplayDownloadError] = useState(false);
  const body = useRef<PsCheckUpdate>();
  const dialogMessage = useRef('');

  // ダイアログ情報取得用param準備
  const param = isViewMode
    ? ({ ps_temporary_state_history_id: state?.ps_temporary_state_history_id } as GetPsCheckParams)
    : ({} as GetPsCheckParams);

  // 入力項目 バリデーション
  const schema = yup.object({
    [PREPARATION_ITEM_INPUT_NAMES.comment]: yup.string().nullable(),
  });

  // 内容確認情報表示取得
  const {
    data: psCheckData,
    isLoading: psCheckIsLoading,
    isError: psCheckIsError,
    error: psCheckError,
  } = useGetPsCheck(versionId, param, options);

  const { handleSubmit, register } = useForm({
    defaultValues: psCheckData?.data,
    resolver: yupResolver(schema),
  });

  /**
   * 押下したボタンの種類を保持しておく
   * @param type 種別(add:確認申請、back:差し戻し)
   */
  const SetButtonType = (type: string) => {
    ButtonType.current = type;
  };

  /**
   * ボタン押下時、確認ダイアログの表示
   */
  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      if (isSubmitting) {
        return;
      }
      e.preventDefault();
      setIsSubmitting(true);

      // 成功時
      const onValid = (data: FieldValues) => {
        // bodyに設定する各プロパティを保持
        body.current = { ...data } as PsCheckUpdate;

        // 差し戻しの場合、必須項目になる入力項目があるため
        if (ButtonType.current === 'back' && body.current.comment === '') {
          setIsSubmitting(false);
          setDisplayValidationError(true);
          dialogMessage.current = getDialogMessage(Message.VALIDATE_INPUT_COMMENT_CHECK_OR_APPROVED, t);
          return;
        }

        // 確認ダイアログ内容準備
        if (ButtonType.current === 'back') {
          dialogMessage.current = getDialogMessage(Message.CONFIRM_BACK_PS_TEMPORARY, t);
        } else {
          dialogMessage.current = getDialogMessage(Message.CONFIRM_CHECK_PS_TEMPORARY, t);
        }
        setIsSubmitting(false);

        // 確認ダイアログの表示
        setConfirmDalogFlg(true);
      };

      // 失敗時
      const onInvalid = () => {
        setDisplayValidationError(true);
        dialogMessage.current = getDialogMessage(Message.VALIDATE_INPUT_COMMENT_CHECK_OR_APPROVED, t);
      };

      void handleSubmit(onValid, onInvalid)();
    },
    [handleSubmit, t, isSubmitting]
  );

  // 差し戻しAPI準備
  const { mutateAsync: checkRemandAsync, error: checkRemandError } = usePostPsCheckRemand(options);
  // 内容確認API準備
  const { mutateAsync: checkCheckAsync, error: saveError } = usePostPsCheckCheck(options);

  /**
   * 確認ダイアログOK押下時、各API実行
   * @param bodyObject
   * @returns
   */
  const DialogOkClick = async () => {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);

    // 差し戻しの場合
    if (ButtonType.current === 'back') {
      await checkRemand();
    }
    // 内容確認の場合
    else if (ButtonType.current === 'add') {
      await checkCheck();
    }
  };

  // 内容確認成功時
  const onSuccessSave = useCallback(async () => {
    // 保存成功したらリフェッチする
    setIsSubmitting(false);
    setConfirmDalogFlg(false);
    setRefresh();
    await checkNextPs(targetIndex);
  }, [setRefresh, checkNextPs, targetIndex]);

  // 差し戻し成功時
  const onSuccessBack = useCallback(async () => {
    setIsSubmitting(false);
    setConfirmDalogFlg(false);
    setRefresh();
    await checkNextPs(targetIndex);
  }, [setRefresh, checkNextPs, targetIndex]);

  // 内容確認失敗時
  const onErrorSave = useCallback(() => {
    setDisplaySpinnerModal(false);
    setConfirmDalogFlg(false);
    setSaveErrorModal(true);
    setIsSubmitting(false);
  }, []);

  // 差し戻し失敗時
  const onErrorRemand = useCallback(() => {
    setDisplaySpinnerModal(false);
    setConfirmDalogFlg(false);
    setRemandErrorModal(true);
    setIsSubmitting(false);
  }, []);

  // 差し戻しする
  const checkRemand = useCallback(async () => {
    const psVersionId = versionId;
    // 差し戻し実行
    await checkRemandAsync(
      {
        psVersionId,
        data: body?.current ?? {},
      },
      { onSuccess: onSuccessBack, onError: onErrorRemand }
    );
  }, [checkRemandAsync, versionId, body, onSuccessBack, onErrorRemand]);

  // 内容確認する
  const checkCheck = useCallback(async () => {
    const psVersionId = versionId;
    // 内容確認実行
    await checkCheckAsync(
      {
        psVersionId,
        data: body?.current ?? {},
      },
      { onSuccess: onSuccessSave, onError: onErrorSave }
    );
  }, [checkCheckAsync, versionId, body, onSuccessSave, onErrorSave]);

  // 添付ファイルダウンロードクリック時
  const paramForDownlad = isViewMode
    ? ({ ps_temporary_state_history_id: state?.ps_temporary_state_history_id } as GetPsCheckDownloadParams)
    : ({} as GetPsCheckDownloadParams);
  const optionsForBinary = useOptionsForBinary(language);
  const { refetch, isError, error } = useGetPsCheckDownload<GetPsCheckDownloadQueryResult, AxiosError<Blob>>(
    versionId,
    paramForDownlad,
    optionsForBinary
  );
  const onPsCheckDownloadClicked = useCallback(async () => {
    setDisplaySpinnerModal(true);
    const response = await refetch();
    setDisplaySpinnerModal(false);
    if (response.data != null) {
      executeDownload(response.data);
    }
  }, [refetch]);

  useEffect(() => setDisplayDownloadError(isError), [isError]);

  // ローディング
  if (psCheckIsLoading) {
    return <SpinnerModal />;
  }

  // エラー時
  if (psCheckIsError || psCheckData?.data == null) {
    return <ErrorModal onClickOk={onClose} error={psCheckError} />;
  }

  // データがない場合
  if (psCheckData?.data == null) {
    return <NoDataComponent />;
  }

  return (
    <>
      <Modal active onClose={onClose} displayCloseButton={displayCloseButton} additionalClassName="popupfr1030">
        <form onSubmit={onSubmit}>
          <div>
            <div className="pop-tablefr1">
              <div>
                <table>
                  {/* 内容確認(GL)ダイアログ 画面 1列目 */}
                  <tbody>
                    <tr>
                      <th>{t('申請日')}</th>
                      <td>{formatDate(truncateDate(new Date()), 'YYYY/MM/DD')}</td>
                    </tr>
                    <tr>
                      <th>{t('作成者')}</th>
                      <td>{psCheckData.data.create_user_name}</td>
                    </tr>
                    <tr>
                      <th>{t('確認者(PS機能)')}</th>
                      <td>{psCheckData.data.check_user_name}</td>
                    </tr>
                    <tr>
                      <th> </th>
                      <td />
                    </tr>
                    <tr>
                      <th>{t('管理No.')}</th>
                      <td>{psCheckData.data.ps_no}</td>
                    </tr>
                    <tr>
                      <th>{t('構成')}</th>
                      <td>
                        {/* [構成]は配列のオブジェクト 改行して1つにまとめる */}
                        {psCheckData.data.composition?.map((val, index) => {
                          if (index === (psCheckData.data.composition?.length ?? 0) - 1) {
                            return <React.Fragment key={val}>{val}</React.Fragment>;
                          }
                          return (
                            <React.Fragment key={val}>
                              {val}
                              <br />
                            </React.Fragment>
                          );
                        })}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div>
                <table>
                  {/* 内容確認(GL)ダイアログ 画面 2列目 */}
                  <tbody>
                    <tr>
                      <th>{t('テキスト確定条件')}</th>
                      <td>{psCheckData.data.text_condition}</td>
                    </tr>
                    <tr>
                      <th>{t('タイトル')}</th>
                      <td>{psCheckData.data.title}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className="pop-tablefr2">
              <table className="poptable1">
                <tbody>
                  <tr className="pop-table-tr-line">
                    <th>
                      <label htmlFor="changeLaunchSection">{t('変更打上部門')}</label>
                    </th>
                    <td>
                      <label>{psCheckData.data.change_request_section}</label>
                    </td>
                  </tr>
                  <tr className="pop-table-tr-line">
                    <th>{t('変更の理由')}</th>
                    <td>
                      <div
                          // eslint-disable-next-line react/no-danger
                          dangerouslySetInnerHTML={{ __html: psCheckData.data.release_note == null ? "" : psCheckData.data.release_note.replace(/\r\n|\n/g, '<br />')}}
                      />
                    </td>
                  </tr>
                  <tr className="pop-table-tr-line">
                    <th>{t('申請の理由')}</th>
                    {/* 編集可能 ただし閲覧モードの場合、表示のみ */}
                    {!isViewMode ? (
                      <td className="non-padding-left">
                        <textarea
                          id="message"
                          rows={4}
                          cols={33}
                          {...register('message')}
                          defaultValue={psCheckData.data.message}
                        />
                      </td>
                    ) : (
                      <td>
                        <div
                          // eslint-disable-next-line react/no-danger
                          dangerouslySetInnerHTML={{ __html: psCheckData.data.message == null ? "" : psCheckData.data.message.replace(/\r\n|\n/g, '<br />')}}
                        />
                      </td>
                    )}
                  </tr>
                  <tr className="pop-table-tr-line non-bottom-input-tr">
                    <th>{t('差戻の理由')}</th>
                    {/* 編集可能 ただし閲覧モードの場合、表示のみ */}
                    <td>
                      {!isViewMode ? (
                        <textarea
                          id="comment"
                          {...register('comment')}
                          rows={4}
                          cols={33}
                          defaultValue={psCheckData.data.comment}
                        />
                      ) : (
                        <td>
                          <div
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{ __html: psCheckData.data.comment == null ? "" : psCheckData.data.comment.replace(/\r\n|\n/g, '<br />')}}
                          />
                        </td>                          
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            {/* フッター部 */}
            <div className="btnfr-all mb0 bfall1 conf-req-btn1fr">
              <div className="btnfr-left1">
                <ul className="ps-dialogbtn" key="0">
                  <li key="1">
                    <Link
                      to={`/${language}/ps/${psCheckData.data.ps_type === 0 || psCheckData.data.ps_type === 1 ? 'difftree' : 'diff'}/${psCheckData.data.newest_ps_version_id?.toString() ?? ''}/${
                        psCheckData.data.ps_version_id?.toString() ?? ''
                      }`}
                      target="_blank"
                    >
                      <i className="fa-solid fa-eye" />
                      {t('PS改廃案表示')}
                    </Link>
                  </li>
                  <li key="2">
                    <Link
                      to={`/${language}/ps/view/${psCheckData.data.newest_ps_version_id?.toString() ?? ''}`}
                      target="_blank"
                    >
                      <i className="fa-solid fa-eye" />
                      {t('最新PS表示')}
                    </Link>
                  </li>
                  {(psCheckData.data.attach_files?.length ?? -1) > 0 && (
                    <button type="button" className="text-button" onClick={onPsCheckDownloadClicked}>
                      <li className="cursor text-button">
                        <i className="fa-solid fa-paperclip" />
                        {t('添付ファイル')}
                      </li>
                    </button>
                  )}
                </ul>
              </div>
              <div className="btnfr-right1">
                <div className="btnfr btnfr4">
                  <div>
                    <button
                      type="button"
                      className="text-button btn2 btnbg-g"
                      onClick={() => {
                        if (!isViewMode) {
                          onClose();
                        } else {
                          setActiveModal(DialogType.STATE_HISTORY);
                        }
                      }}
                    >
                      <span className="modal-open cursor">{!isViewMode ? t('キャンセル') : t('閉じる')}</span>
                    </button>
                    {/* 差し戻しボタン、確認ボタンは閲覧モードの場合非表示 */}
                    {!isViewMode && (
                      <>
                        <button type="submit" className="iconbf text-button btn2" onClick={() => SetButtonType('back')}>
                          <i className="fa-solid fa-rotate-left" />
                          <span className="btn1 dialog-btn">{t('差し戻し') ?? ''}</span>
                        </button>
                        <button type="submit" className="iconbf2 text-button btn2" onClick={() => SetButtonType('add')}>
                          <i className="fa-solid fa-circle-check" />
                          <span className="btn1 dialog-btn  circle-check-solid">{t('確認') ?? ''}</span>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </Modal>
      {/* 確認メッセージ */}
      {confirmDalogFlg && (
        <ConfirmModal
          onClickOk={() => {
            if (body === undefined || body.current === undefined) {
              return;
            }
            void DialogOkClick();
          }}
          onClickCancel={() => {
            setConfirmDalogFlg(false);
          }}
        >
          <div className="poptxt">
            <p>{dialogMessage.current}</p>
          </div>
        </ConfirmModal>
      )}
      {/* スピナーモーダル */}
      {displaySpinnerModal && <SpinnerModal />}
      {/* バリデーションエラーモーダル */}
      {displayValidationError && (
        <OkModal onClickOk={() => setDisplayValidationError(false)}>{dialogMessage.current}</OkModal>
      )}
      {/* 差し戻しエラーの表示 */}
      {remandErrorModal && (
        <ErrorModal onClickOk={() => setRemandErrorModal(false)} error={checkRemandError}>
          {getDialogMessage(Message.FAILURE_BACK_PS_TEMPORARY, t)}
        </ErrorModal>
      )}
      {/* 確認エラーの表示 */}
      {saveErrorModal && (
        <ErrorModal onClickOk={() => setSaveErrorModal(false)} error={saveError}>
          {getDialogMessage(Message.FAILURE_CHECK_PS_TEMPORARY, t)}
        </ErrorModal>
      )}
      {/* ダウンロードエラー */}
      {displayDownloadError && <BlobErrorModal error={error} onClickOk={() => setDisplayDownloadError(false)} />}
    </>
  );
}

export default PsCheckDialog;
